import {UserModel} from 'app/modules/auth'
import {Option} from 'app/pages/school/settings/utils/settingsData'
import axios from 'axios'
import constants, {
  ATTENDANCE_STATUS,
  BASE_HOSTS,
  CERTIFICATE_TYPES,
  CLASSES_STATUS,
  CLASSES_SUBJECT_STATUS,
  CLASS_WISE_INSTANCE,
  DEFAULT_TENANT_VALUE,
  DIGITAL_REQUEST_OBJECT_TYPE,
  DIGITAL_REQUEST_STATUS,
  DIGITAL_REQUEST_TYPE,
  GENDER,
  GENERAL,
  GRADE_STATUS,
  HOMEWORK_RESULTS,
  INVOICE_STATUS,
  INVOICE_TYPE,
  LIBRARY_STATUS,
  MODULE_PERMISSIONS,
  M_LAYOUT,
  QUESTION_TYPES,
  REGISTRATION_STATUS,
  SECRET_KEY,
  SHOW_ILMVERSITY_LOGO,
  SUBJECTS_STATUS,
  SUBJECT_WISE_INSTANCE,
  TEACHER_MARITAL_STATUS,
  TENANT_CUSTOM_DOMAINS,
  TENANT_CUSTOM_IFRAME_URL,
  TENANT_CUSTOM_IMAGES,
  TEST_RESULTS,
  USER_INACTIVE_REASON,
  USER_TYPES,
  USER_TYPES_NUMERIC,
  VENDOR_STATUS,
  base_url,
} from 'common/constants'
import customMappings from 'common/customTitles/mappings.json'
import {putToSignedUrl} from 'common/helpers/signedUpload'
import _ from 'lodash'
import moment from 'moment'
import {createElement} from 'react'
import {IntlShape} from 'react-intl'
import {Navigate, useLocation} from 'react-router'
import {
  confirmFileUpload,
  deleteDocument,
  getSignedUploadUrl,
} from 'setup/redux/Actions/Document/document'
import store from 'setup/redux/Store'
import Swal from 'sweetalert2'

import {toAbsoluteUrl} from '_metronic/helpers'
import {getLang} from '_metronic/i18n/Metronici18n'
import {getActiveMessages} from '_metronic/i18n/messageRegistry'
import {getTheme} from '_metronic/partials'

export const getAppEnv = () => import.meta.env.VITE_APP_ENV || 'local'

export const toRomanNumeral = (num: number): string => {
  const romanNumerals: Record<number, string> = {
    1: 'I',
    2: 'II',
    3: 'III',
    4: 'IV',
    5: 'V',
    6: 'VI',
    7: 'VII',
    8: 'VIII',
    9: 'IX',
    10: 'X',
    11: 'XI',
    12: 'XII',
    13: 'XIII',
    14: 'XIV',
    15: 'XV',
    16: 'XVI',
    17: 'XVII',
    18: 'XVIII',
    19: 'XIX',
    20: 'XX',
  }
  return romanNumerals[num] || String(num)
}

export const fieldsFilled = ({...stateObject}) => {
  const flag = Object.values(stateObject).every((x) => x !== null && x !== '')
  return flag
}

export function emailIsValid(email: string) {
  return /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)
}

export const formatDate = (date: Date, format = 'YYYY-MM-DD') =>
  date ? moment(date, format).format('DD MMM YYYY') : ''

export const formatTime = (date: Date) => (date ? moment(date).format('hh:mm a') : '')

const ATTENDANCE_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

export const toAttendanceDateTime = (value: string | Date | null | undefined) => {
  if (!value) return value
  return moment(value).format(ATTENDANCE_DATE_TIME_FORMAT)
}

export const formatAttendanceDateTime = (
  value: string | Date | null | undefined,
  format: string
) => {
  if (!value) return ''

  if (typeof value === 'string') {
    const dateTimeMatch = value.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}:\d{2}(?::\d{2})?)/)

    if (dateTimeMatch) {
      const time =
        dateTimeMatch[2].split(':').length === 2 ? `${dateTimeMatch[2]}:00` : dateTimeMatch[2]
      return moment(`${dateTimeMatch[1]} ${time}`, ATTENDANCE_DATE_TIME_FORMAT).format(format)
    }
  }

  return moment(value).format(format)
}

export const getDateOnlyString = (date: string | null | undefined) => {
  if (!date) return ''

  const match = String(date)
    .trim()
    .match(/^(\d{4}-\d{2}-\d{2})/)
  return match?.[1] || ''
}

export const formatDateOnly = (date: string | null | undefined, format = 'LL', fallback = '') => {
  const dateOnly = getDateOnlyString(date)
  if (!dateOnly || dateOnly === '0000-00-00') return fallback

  const parsedDate = moment(dateOnly, 'YYYY-MM-DD', true)
  return parsedDate.isValid() ? parsedDate.format(format) : fallback
}

export const toTitleCase = (str: string) =>
  str
    ?.split('_')
    .join(' ')
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())

export const getTenantNameFromCustomDomain = (customDomain: string): string | null => {
  const url = customDomain.startsWith('www.') ? customDomain.replace('www.', '') : customDomain
  for (const key in TENANT_CUSTOM_DOMAINS) {
    if (Object.hasOwnProperty.call(TENANT_CUSTOM_DOMAINS, key)) {
      const index = key as keyof typeof TENANT_CUSTOM_DOMAINS
      const customDomains = TENANT_CUSTOM_DOMAINS[index]
      if (customDomains.includes(url)) {
        return key
      }
    }
  }
  return null
}

/** Returns true when hostname is ilmversity.ai or da1timetable.ai (used for auth gradient styling) */
export const isAuthGradientDomain = (): boolean => {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  return hostname.includes('ilmversity.ai') || hostname.includes('da1timetable.ai')
}

export const getTenantName = (): string => {
  const reduxStore = store.getState()
  const userInfo = reduxStore.userReducer

  let tenantName = `${userInfo.tenant ? userInfo.tenant : DEFAULT_TENANT_VALUE}`

  const hostname = window.location.hostname
  if (!BASE_HOSTS.some((baseHost) => hostname.includes(baseHost))) {
    const tenantNameFromCustomDomain = getTenantNameFromCustomDomain(window.location.hostname)
    if (tenantNameFromCustomDomain) {
      tenantName = tenantNameFromCustomDomain
    }
  }

  return tenantName
}

export const getTenantDomain = (tenantName?: string): string => {
  const env = getAppEnv()
  let tld
  switch (env) {
    case 'prod':
      tld = 'net'
      break
    case 'staging':
      tld = 'dev'
      break
    default:
      tld = 'test'
  }

  return `${tenantName || getTenantName()}.ilmversity.${tld}`
}

/** Suffix heads that make a 3-label public suffix (edu.pk, com.pk, co.uk, …). */
const MULTI_LABEL_SUFFIX_HEADS = ['com', 'edu', 'org', 'net', 'gov', 'co', 'ac', 'sch', 'mil']

/** eTLD+1, e.g. `miles.rootsinternational.edu.pk` -> `rootsinternational.edu.pk` */
export const getRegistrableDomain = (hostname: string): string => {
  const labels = hostname
    .toLowerCase()
    .replace(/^www\./, '')
    .split('.')
  if (labels.length <= 2) return labels.join('.')

  const tld = labels[labels.length - 1]
  const suffixLabels =
    tld.length === 2 && MULTI_LABEL_SUFFIX_HEADS.includes(labels[labels.length - 2]) ? 3 : 2

  return labels.slice(-suffixLabels).join('.')
}

const isSameSiteAsCurrentHost = (host: string): boolean => {
  if (typeof window === 'undefined') return false
  return getRegistrableDomain(host) === getRegistrableDomain(window.location.hostname)
}

export const getTenantCustomDomain = (): string => {
  const tenantName = getTenantName()
  const env = getAppEnv()

  if (env === 'local') return getTenantDomain()

  // staging: sibling of the portal host so the PHP session cookie is first-party (Safari-safe)
  if (env === 'staging') return `${tenantName}.legacy.ilmversity.online`

  const dedicatedHosts = TENANT_CUSTOM_IFRAME_URL[tenantName] ?? []
  const sharedHost = `${tenantName}.legacy.ilmversity.com`

  if (!dedicatedHosts.length) return sharedHost

  // prefer a first-party host, else the PHP session cookie is third-party and browsers drop it
  return [...dedicatedHosts, sharedHost].find(isSameSiteAsCurrentHost) ?? dedicatedHosts[0]
}

/** Root product hosts used for universal login (not school subdomains). */
export const UNIVERSAL_LOGIN_ROOT_HOSTS = [
  'da1timetable.ai',
  'da1timetable.info',
  'ilmversity.ai',
] as const

/** True when the user is on the universal login entry host (e.g. da1timetable.ai root). */
export const isUniversalLoginHost = (): boolean => {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname.replace(/^www\./, '')
  if (hostname.startsWith('auth.')) return false

  // Dev: bare localhost uses universal login (not tenant subdomains like school.localhost).
  // if (hostname === 'localhost' || hostname === '127.0.0.1') {
  //   return true
  // }

  return UNIVERSAL_LOGIN_ROOT_HOSTS.includes(
    hostname as (typeof UNIVERSAL_LOGIN_ROOT_HOSTS)[number]
  )
}

/**
 * Legacy Da1 marketing roots that previously hosted universal login at /login.
 * Redirects from these hosts go to auth.da1timetable.ai / .info.
 */
export const isDa1LegacyMarketingLoginHost = (): boolean => {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname.replace(/^www\./, '')
  if (hostname.startsWith('auth.')) return false
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false
  return hostname === 'da1timetable.ai' || hostname === 'da1timetable.info'
}

/** @deprecated Use isDa1LegacyMarketingLoginHost or isDa1AuthUniversalLoginHost */
export const isDa1UniversalLoginHost = isDa1LegacyMarketingLoginHost

/** Da1 central auth host for universal login (auth.da1timetable.ai / .info). */
export const isDa1AuthUniversalLoginHost = (): boolean => {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  return hostname === 'auth.da1timetable.ai' || hostname === 'auth.da1timetable.info'
}

export const DA1_UNIVERSAL_LOGIN_PATH = '/login'

/** Full URL for Da1 universal login on the central auth domain. */
export const getDa1AuthUniversalLoginUrl = (): string => {
  const authHost =
    typeof window !== 'undefined' && window.location.hostname.includes('da1timetable.info')
      ? 'auth.da1timetable.info'
      : 'auth.da1timetable.ai'
  return `https://${authHost}${DA1_UNIVERSAL_LOGIN_PATH}`
}

/** Login path for the current host (auth /login vs institute /auth/login). */
export const getPublicLoginPath = (): string => {
  if (isDa1AuthUniversalLoginHost()) return DA1_UNIVERSAL_LOGIN_PATH
  if (isDa1LegacyMarketingLoginHost()) return '/login'
  return '/auth/login'
}

/** Primary custom domain for a tenant slug, if configured in TENANT_CUSTOM_DOMAINS. */
export const getCustomDomainForTenant = (tenantSlug: string): string | null => {
  const customDomains = TENANT_CUSTOM_DOMAINS[tenantSlug as keyof typeof TENANT_CUSTOM_DOMAINS]
  return customDomains?.[0] ?? null
}

/** Tenant slug inferred from the current hostname (null on universal/auth hosts). */
export const getHostnameTenantSlug = (): string | null => {
  if (typeof window === 'undefined') return null
  if (isUniversalLoginHost()) return null

  const hostname = window.location.hostname.replace(/^www\./, '')
  if (hostname.startsWith('auth.')) return null

  if (BASE_HOSTS.some((baseHost) => hostname.includes(baseHost))) {
    const slug = hostname.split('.')[0]
    const productSlugs = UNIVERSAL_LOGIN_ROOT_HOSTS.map((host) => host.split('.')[0])
    if (productSlugs.includes(slug)) return null
    return slug
  }

  return getTenantNameFromCustomDomain(hostname)
}

const getPortalBaseDomain = (): string => {
  const envOverride = import.meta.env.VITE_TENANT_PORTAL_BASE_DOMAIN as string | undefined
  if (envOverride) return envOverride

  const hostname = window.location.hostname.replace(/^www\./, '')
  if (hostname.includes('localhost')) {
    return 'localhost'
  }
  if (hostname.includes('da1timetable.info')) return 'da1timetable.info'
  if (hostname.includes('da1timetable.ai')) return 'da1timetable.ai'
  if (hostname.includes('ilmversity.ai')) return 'ilmversity.ai'
  return 'da1timetable.ai'
}

/** School portal hostname for selective-AI product tenants (e.g. schoola.da1timetable.ai). */
export const getTenantPortalHost = (tenantSlug: string): string => {
  const customDomain = getCustomDomainForTenant(tenantSlug)
  if (customDomain) return customDomain

  const baseDomain = getPortalBaseDomain()
  if (baseDomain === 'localhost') {
    return `${tenantSlug}.localhost`
  }
  return `${tenantSlug}.${baseDomain}`
}

/** Full URL to hand off a session to a tenant workspace via rauth_token. */
export const getTenantPortalLoginRedirectUrl = (
  tenantSlug: string,
  accessToken: string,
  path = '/dashboard',
  searchParams?: Record<string, string>
): string => {
  const host = getTenantPortalHost(tenantSlug)
  const protocol =
    host.includes('localhost') && typeof window !== 'undefined'
      ? window.location.protocol
      : 'https:'
  const url = new URL(path, `${protocol}//${host}`)
  url.searchParams.set('rauth_token', accessToken)
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      url.searchParams.set(key, value)
    }
  }
  return url.href
}

export const getTenantCustomImage = (
  image: 'background_image' | 'big_logo' | 'small_logo'
): string | undefined => {
  const tenantName = getTenantName()
  const tenantImages = TENANT_CUSTOM_IMAGES[tenantName]
  return tenantImages?.[image]
}

export const getTenantLogo = (size: 'big' | 'small', themeOverride?: 'dark' | 'light') => {
  let tl = size === 'big' ? getTenantCustomImage('big_logo') : getTenantCustomImage('small_logo')

  if (tl) return convertS3Url(tl)

  const reduxStore = store.getState()
  const {generalSettings} = reduxStore.mainReducer

  tl =
    size === 'big'
      ? (generalSettings?.bigLogo ?? generalSettings?.smallLogo)
      : (generalSettings?.smallLogo ?? generalSettings?.bigLogo)
  if (tl) tl = convertS3Url(tl)

  // Only fall back once the server has answered. Before that an empty store means
  // "not asked yet", not "this tenant has no logo", and showing the Ilmversity logo
  // to a branded tenant for a frame is exactly the flash we are removing.
  if (!tl && SHOW_ILMVERSITY_LOGO && reduxStore.mainReducer.generalSettingsLoaded) {
    let theme = getTheme()
    if (themeOverride) {
      theme = themeOverride
    }
    if (theme === 'dark') {
      tl = toAbsoluteUrl('/media/logos/logo-1-dark.svg')
    } else {
      tl = toAbsoluteUrl('/media/logos/logo-1.svg')
    }
  }

  return tl
}

/** Graduation-cap icon for collapsed sidebar (and similar compact UI). */
export const getTenantIconLogo = () => {
  if (!SHOW_ILMVERSITY_LOGO) {
    return undefined
  }

  return toAbsoluteUrl('/media/logos/logo-icon.svg')
}

export const getActiveSession = (): any => {
  let session = localStorage.getItem(constants.session_db_postfix_key)
  if (session) {
    session = JSON.parse(session)
  }
  return session || null
}

export const hasPermission = (permission: string) => {
  const reduxStore = store.getState()
  const {permissions} = reduxStore.userReducer
  for (let i = 0; i < permissions.length; i++) {
    const perm = permissions[i]

    if (
      perm.key === permission ||
      (perm.permissions && perm.permissions.includes(permission)) ||
      (perm.sections && perm.sections.includes(permission))
    )
      return true
  }

  return false
}

export const toPascalCase = (str: string) => {
  // split the string into words
  const words = str.split('/')

  // transform each word to PascalCase
  const pascalWords = words.map((word: string) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  })

  // join the words and return the result
  return pascalWords.join('')
}

export const sanitizeHTML = (str: string, maxLength: number) => {
  if (!str || typeof str !== 'string') {
    return ''
  }

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = str
  let sanitizedText = tempDiv.textContent || tempDiv.innerText || ''

  if (!sanitizedText.trim()) {
    return ''
  }

  if (sanitizedText.length > maxLength) {
    sanitizedText = sanitizedText.substring(0, maxLength) + '...'
  }

  return sanitizedText
}

export const getTokenLink = () => {
  const reduxStore = store.getState()
  const {accessToken} = reduxStore.userReducer
  const branch = localStorage.getItem('branch')
  const activeSession = getActiveSession()

  return `?rauth_token=${accessToken}&mlayout=false&branchId=${branch ? branch : '0'}${
    activeSession?.db_postfix ? `&db_postfix=${activeSession.db_postfix}` : ''
  }`
}

export const isM_Layout = () => {
  return sessionStorage.getItem(M_LAYOUT) === 'true'
}

export const isAdmin = () => {
  const reduxStore = store.getState()
  const {userType} = reduxStore.userReducer
  return userType === 'ADMIN'
}

export const shouldShowAiCredits = () => {
  const reduxStore = store.getState()
  const {aiCredits} = reduxStore.userReducer
  return (
    (isAIDomain() || isComDomain() || isOnlineDomain() || isInfoDomain()) &&
    aiCredits?.remaining_credits !== null &&
    aiCredits?.remaining_credits !== undefined &&
    aiCredits?.remaining_credits >= 0
  )
}

export const isTeacher = () => {
  const reduxStore = store.getState()
  const {userType} = reduxStore.userReducer
  return userType === 'TEACHER'
}

export const isStaff = () => {
  const reduxStore = store.getState()
  const {userType} = reduxStore.userReducer
  return userType === 'STAFF'
}

export const isStudent = () => {
  const reduxStore = store.getState()
  const {userType} = reduxStore.userReducer
  return userType === 'STUDENT'
}

export const isParent = () => {
  const reduxStore = store.getState()
  const {userType} = reduxStore.userReducer
  return userType === 'PARENT'
}

export const changeFavicon = (url: string) => {
  // The real icon has arrived, so the tab spinner from index.html has nothing left to say.
  ;(window as any).__stopFaviconSpinner?.()

  const upsertLink = (
    rel: string,
    attrs: Record<string, string> = {},
    selector: string = `link[rel="${rel}"]`
  ) => {
    let link = document.head.querySelector(selector) as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', rel)
      document.head.appendChild(link)
    }
    link.setAttribute('href', url)
    Object.entries(attrs).forEach(([key, value]) => link!.setAttribute(key, value))
  }

  // Provide explicit larger sizes so the favicon appears crisper / less "tiny" across browsers.
  upsertLink('icon', {type: 'image/png', sizes: '32x32'}, 'link[rel="icon"]')
  upsertLink('shortcut icon', {type: 'image/png', sizes: '32x32'}, 'link[rel="shortcut icon"]')
  upsertLink('apple-touch-icon', {sizes: '180x180'}, 'link[rel="apple-touch-icon"]')
}

export const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min)) + min
}

export const getVendorStatusString = (status: number) => {
  switch (status) {
    case VENDOR_STATUS.PENDING:
      return 'GENERAL.PENDING'
    case VENDOR_STATUS.ACTIVE:
      return 'GENERAL.ACTIVE'
    case VENDOR_STATUS.BLOCKED:
      return 'GENERAL.BLOCKED'
    default:
      return 'GENERAL.PENDING'
  }
}

export const getLibraryStatusString = (status: number) => {
  switch (status) {
    case LIBRARY_STATUS.ACTIVE:
      return 'GENERAL.ACTIVE'
    case LIBRARY_STATUS.INACTIVE:
      return 'GENERAL.INACTIVE'
    default:
      return 'GENERAL.PENDING'
  }
}

export const getCertificateTypeString = (type: number) => {
  switch (type) {
    case CERTIFICATE_TYPES.STUDENT:
      return 'CERTIFICATE_TYPES.STUDENT'
    case CERTIFICATE_TYPES.HR:
      return 'CERTIFICATE_TYPES.HR'
    case CERTIFICATE_TYPES.CCA_CERTIFICATE:
      return 'CERTIFICATE_TYPES.CCA_CERTIFICATE'
    default:
      return 'N/A'
  }
}

export const getUserTypeName = (type: any) => {
  const userType = Object.entries(USER_TYPES_NUMERIC).find(([, value]) => value == type)
  return userType && userType[0] ? userType[0] : null
}

export const getUserInactiveReasonLabel = (reason: number, customReason?: string) => {
  switch (reason) {
    case USER_INACTIVE_REASON.INACTIVE:
      return 'USER.INACTIVE_REASON.INACTIVE'
    case USER_INACTIVE_REASON.NEW_ADMISSION:
      return 'USER.INACTIVE_REASON.NEW_ADMISSION'
    case USER_INACTIVE_REASON.ALUMNI:
      return 'USER.INACTIVE_REASON.ALUMNI'
    case USER_INACTIVE_REASON.OTHER:
      return customReason || 'MENU.OTHER'
    default:
      return 'N/A'
  }
}

export const getDigitalRequestStatusString = (status: number) => {
  switch (status) {
    case DIGITAL_REQUEST_STATUS.REJECTED:
      return 'DIGITAL_REQUEST_STATUS.REJECTED'
    case DIGITAL_REQUEST_STATUS.PENDING:
      return 'DIGITAL_REQUEST_STATUS.PENDING'
    case DIGITAL_REQUEST_STATUS.APPROVED:
      return 'DIGITAL_REQUEST_STATUS.APPROVED'
    case DIGITAL_REQUEST_STATUS.COMPLETE:
      return 'DIGITAL_REQUEST_STATUS.COMPLETE'
    default:
      return 'DIGITAL_REQUEST_STATUS.PENDING'
  }
}

export const getDigitalRequestStatusBadgeColor = (status: number) => {
  switch (status) {
    case DIGITAL_REQUEST_STATUS.REJECTED:
      return 'badge-light-danger'
    case DIGITAL_REQUEST_STATUS.PENDING:
      return 'badge-light-primary'
    case DIGITAL_REQUEST_STATUS.APPROVED:
      return 'badge-light-success'
    case DIGITAL_REQUEST_STATUS.COMPLETE:
      return 'badge-light-warning'
    default:
      return 'badge-light-primary'
  }
}

export const getDigitalRequestTypeString = (status: number) => {
  switch (status) {
    case DIGITAL_REQUEST_TYPE.CLASS_LEAVE:
      return 'DIGITAL_REQUEST_TYPE.CLASS_LEAVE'
    case DIGITAL_REQUEST_TYPE.ASSESSMENT_LEAVE:
      return 'DIGITAL_REQUEST_TYPE.ASSESSMENT_LEAVE'
    default:
      return 'DIGITAL_REQUEST_TYPE.CLASS_LEAVE'
  }
}

export const getDigitalRequestTypeBadgeColor = (status: number) => {
  switch (status) {
    case DIGITAL_REQUEST_TYPE.CLASS_LEAVE:
      return 'badge-light-info'
    case DIGITAL_REQUEST_TYPE.ASSESSMENT_LEAVE:
      return 'badge-light-primary'
    default:
      return 'badge-light-primary'
  }
}

export const getDigitalRequestObjectTypeBadgeColor = (type: number) => {
  switch (type) {
    case DIGITAL_REQUEST_OBJECT_TYPE.SUBJECT_LEAVE:
      return 'badge-light-primary'

    case DIGITAL_REQUEST_OBJECT_TYPE.EXAM_LEAVE:
      return 'badge-light-success'

    case DIGITAL_REQUEST_OBJECT_TYPE.TEST_LEAVE:
      return 'badge-light-info'

    case DIGITAL_REQUEST_OBJECT_TYPE.ASSIGNMENT_LEAVE:
      return 'badge-light-warning'

    default:
      return 'badge-light-primary'
  }
}

export const getUserTypeBadgeColor = (type: string) => {
  switch (type) {
    case USER_TYPES.ADMIN:
      return 'badge-light-primary'
    case USER_TYPES.TEACHER:
      return 'badge-light-danger'
    case USER_TYPES.STAFF:
      return 'badge-light-warning'
    case USER_TYPES.STUDENT:
      return 'badge-light-info'
    case USER_TYPES.PARENT:
      return 'badge-light-success'

    default:
      return 'badge-light-secondary'
  }
}

export const getUserTypeNumericBadgeColor = (type: number) => {
  switch (type) {
    case USER_TYPES_NUMERIC.ADMIN:
      return 'badge-light-primary'
    case USER_TYPES_NUMERIC.TEACHER:
      return 'badge-light-danger'
    case USER_TYPES_NUMERIC.STAFF:
      return 'badge-light-warning'
    case USER_TYPES_NUMERIC.STUDENT:
      return 'badge-light-info'
    case USER_TYPES_NUMERIC.PARENT:
      return 'badge-light-success'

    default:
      return 'badge-light-secondary'
  }
}

export const getUserTypeString = (type: number) => {
  switch (type) {
    case USER_TYPES_NUMERIC.ADMIN:
      return 'MENU.ADMINISTRATION'
    case USER_TYPES_NUMERIC.TEACHER:
      return 'MENU.TEACHER'
    case USER_TYPES_NUMERIC.STAFF:
      return 'MENU.STAFF'
    case USER_TYPES_NUMERIC.STUDENT:
      return 'MENU.STUDENTS'
    case USER_TYPES_NUMERIC.PARENT:
      return 'MENU.PARENTS'

    default:
      return 'MENU.ADMINISTRATION'
  }
}

export const getUserProfileLink = (type: number, row: any) => {
  switch (type) {
    case USER_TYPES_NUMERIC.ADMIN:
      return `/profile`
    case USER_TYPES_NUMERIC.TEACHER:
      return `/institute/teacher/${row.teacher_id}`
    case USER_TYPES_NUMERIC.STAFF:
      return `/institute/staff/${row.staff_id}`
    case USER_TYPES_NUMERIC.STUDENT:
      return `/institute/student/${row.student_id}`
    case USER_TYPES_NUMERIC.PARENT:
      return `/institute/parent/${row.parent_id}`

    default:
      return `/profile`
  }
}

export const getProfileLinkForUserType = (type: string, row: any) => {
  switch (type) {
    case 'teacher':
      return `/institute/teacher/${row.teacher_id}/info`
    case 'staff':
      return `/institute/staff/${row.staff_id}/info`
    case 'student':
      return `/institute/student/${row.student_id}/info`
    default:
      return `/`
  }
}

export const getUserProfileLinkForMarkedBy = (type: number, row: any) => {
  switch (type) {
    case USER_TYPES_NUMERIC.ADMIN:
      return `/profile`
    case USER_TYPES_NUMERIC.TEACHER:
      return `/institute/teacher/${row.marked_by_id}`
    case USER_TYPES_NUMERIC.STAFF:
      return `/institute/staff/${row.marked_by_id}`

    default:
      return `/profile`
  }
}

export const getUserProfileLinkForMentorCounseller = (
  type: number,
  row: any,
  mentor: boolean = true
) => {
  switch (type) {
    case USER_TYPES_NUMERIC.ADMIN:
      return `/profile`
    case USER_TYPES_NUMERIC.TEACHER:
      if (mentor) {
        return `/institute/teacher/${row.mentor_id}`
      }
      return `/institute/teacher/${row.counseller_id}`
    case USER_TYPES_NUMERIC.STAFF:
      if (mentor) {
        return `/institute/staff/${row.mentor_id}`
      }
      return `/institute/staff/${row.counseller_id}`

    default:
      return `/profile`
  }
}

export const getClassStatusBadgeClass = (status: number): string => {
  switch (status) {
    case CLASSES_STATUS.INACTIVE:
      return 'warning'
    case CLASSES_STATUS.ACTIVE:
      return 'success'
    default:
      return ''
  }
}

export function customTitle(key: any) {
  const mappings = customMappings
  const mainStore = store.getState()
  const selectedLang = getLang().selectedLang
  const customNames = mainStore.mainReducer.custom_titles
  let isTrasnlated = false
  let response = ''
  const mapping = mappings[key as keyof typeof mappings]
  if (mapping && customNames) {
    const customTitleData = customNames[mapping.moduleId]
    if (customTitleData) {
      const selectedLangData = customTitleData[selectedLang]
      if (selectedLangData) {
        let cardinalityWord = ''
        if (mapping.cardinality === 'singular') {
          cardinalityWord = selectedLangData.singular
        } else {
          cardinalityWord = selectedLangData.plural
        }

        const sentence = mapping.sentences[selectedLang as keyof typeof mapping.sentences]
        if (cardinalityWord) {
          response = sentence.replace(/\[.*\]/g, cardinalityWord)
          isTrasnlated = true
        }
      } else {
        response = key
      }
    } else {
      response = key
    }
  } else {
    response = key
  }
  if (!isTrasnlated) {
    // Messages of the active locale, loaded by I18nProvider.
    const langDictionary = getActiveMessages()
    if (langDictionary[key]) {
      response = langDictionary[key]
    }
  }
  return response
}

export const parseDocumentObject = (document: any) => {
  return document.url
}

export const getClassesList = (level_name?: boolean): Option[] => {
  const reduxStore = store.getState()
  const {classes} = reduxStore.mainReducer
  const filteredClasses =
    classes &&
    classes
      .filter((item: any) => item.status === 1)
      .map((item: any) => ({
        label: `${item?.name} ${level_name ? '(' + item.level_name + ' - ' + item.branch_name + ')' : item.branch_name}`,
        value: item?.id,
      }))

  return filteredClasses || []
}

export const getSubjectsList = (): Option[] => {
  const reduxStore = store.getState()
  const {get_user_subjects} = reduxStore.mainReducer
  const filteredSubjects =
    get_user_subjects &&
    get_user_subjects
      .filter((item: any) => item.status === 1)
      .map((item: any) => ({
        label: `${item.name} (${item.class_name}) (${item.level_name} - ${item.branch_name})`,
        value: item?.id,
        classId: item?.classId,
      }))

  return filteredSubjects || []
}

export const getActiveClasses = (classes: any[]): Option[] => {
  return (
    classes
      ?.filter((item) => item.status === 1)
      .map((item) => ({
        label: `${item.name} ${item.branch_name}`,
        value: item.id,
      })) || []
  )
}

type DownloadFileProps = {
  path: string
  fileName?: string
  method?: 'GET' | 'POST'
  body?: any
  onSuccess?: (res: any) => void
  onFailure?: (err: any) => void
}
export const downloadFile = ({
  path,
  fileName,
  method = 'GET',
  body,
  onSuccess,
  onFailure,
}: DownloadFileProps) => {
  const userReducer = store.getState().userReducer

  const axiosConfig: any = {
    method: method,
    url: `${base_url}${path}`,
    headers: {
      Authorization: `Bearer ${userReducer.accessToken}`,
      domain: getTenantDomain(),
      platform: 'web',
      platformkey: SECRET_KEY['web'],
      version: '1.0.0',
    },
    responseType: 'blob',
  }

  const activeSession = getActiveSession()

  if (activeSession?.db_postfix) {
    axiosConfig.headers['session-postfix'] = activeSession.db_postfix
  }

  const branchId = localStorage.getItem('branch')

  if (branchId) {
    axiosConfig.headers['branch-id'] = branchId
  }

  if (method === 'POST') {
    axiosConfig.data = body
  }

  axios(axiosConfig)
    .then((response) => {
      const contentDisposition = response.headers['content-disposition']
      const responseFileName = contentDisposition
        ?.split(';')[1]
        .split('=')[1]
        .trim()
        .replaceAll('"', '')

      const blob = new Blob([response.data], {type: response.headers['content-type']})

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName || responseFileName

      document.body.appendChild(a)
      a.click()
      a.remove()

      if (typeof onSuccess === 'function') {
        onSuccess(response)
      }
    })
    .catch((err) => {
      if (typeof onFailure === 'function') {
        onFailure(err)
      }
    })
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const downloadFileFromUrl = ({path, fileName, onSuccess, onFailure}: DownloadFileProps) => {
  const userReducer = store.getState().userReducer
  const activeSession = getActiveSession()

  axios
    .get(`${base_url}${path}`, {
      headers: {
        Authorization: `Bearer ${userReducer.accessToken}`,
        domain: getTenantDomain(),
        platform: 'web',
        platformkey: SECRET_KEY['web'],
        version: '1.0.0',
        ...(activeSession?.db_postfix ? {'session-postfix': activeSession.db_postfix} : {}),
      },
      responseType: 'json',
    })
    .then((response) => {
      const element = document.createElement('a')
      element.style.display = 'none'
      element.setAttribute('href', response.data.url)
      element.setAttribute('download', response.data.name)
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
      //const contentDisposition = response.headers['content-disposition']
      // const responseFileName = contentDisposition
      //   ?.split(';')[1]
      //   .split('=')[1]
      //   .trim()
      //   .replaceAll('"', '')

      // const blob = new Blob([response.data], {type: response.headers['content-type']})
      // console.log(responseFileName)
      // const url = window.URL.createObjectURL(blob)
      // const a = document.createElement('a')
      // a.href = url
      // a.download = fileName || responseFileName
      // document.body.appendChild(a)
      // a.click()
      // a.remove()

      if (typeof onSuccess === 'function') {
        onSuccess(response)
      }
    })
    .catch((err) => {
      if (typeof onFailure === 'function') {
        onFailure(err)
      }
    })
}

export const CustomDownloadFile = (
  content: string,
  filename: string,
  mimeType: string = 'text/plain'
) => {
  const blob = new Blob([content], {type: mimeType})
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const getGradeStatusString = (status: number) => {
  switch (status) {
    case GRADE_STATUS.ACTIVE:
      return 'GENERAL.ACTIVE'
    case GRADE_STATUS.INACTIVE:
      return 'GENERAL.INACTIVE'
    default:
      return 'GENERAL.PENDING'
  }
}

export const getClassStatusString = (status: number) => {
  switch (status) {
    case CLASSES_STATUS.ACTIVE:
      return 'GENERAL.ACTIVE'
    case CLASSES_STATUS.INACTIVE:
      return 'GENERAL.INACTIVE'
    default:
      return 'GENERAL.PENDING'
  }
}

export const getAllowedGradesList = (): Option[] => {
  const reduxStore = store.getState()
  const {classes} = reduxStore.mainReducer

  const uniqueGradeIds = new Set()

  const filteredGrades =
    classes &&
    classes
      .filter((item: any) => {
        if (item.status === 1 && !uniqueGradeIds.has(item.gradeId)) {
          uniqueGradeIds.add(item.gradeId)
          return true
        }
        return false
      })
      .map((item: any) => ({
        label: `${item?.grade_name} (${item.level_name}) (${item.branch_name})`,
        value: item?.gradeId,
      }))

  return filteredGrades || []
}

export const getBranchNameById = (_branchId: string | number | undefined = 1): string => {
  const reduxStore = store.getState()
  const {branches: branchList} = reduxStore.mainReducer

  const branchId = +_branchId
  const selectedBranch = branchList.find((item: any) => item.id === branchId)

  return selectedBranch?.name
}

export const getLevelNamesByIds = (levelIds: string | undefined): string[] => {
  if (!levelIds) return []

  const reduxStore = store.getState()
  const {levels} = reduxStore.mainReducer

  const levelIdsArray = levelIds.split(',').map((id) => parseInt(id.trim()))
  const selectedLevels = levels.filter((item: any) => levelIdsArray.includes(item.id))
  return selectedLevels.map((level: any) => level.name)
}

export const getModuleNamesByIds = (moduleIds: string | undefined): string[] => {
  if (!moduleIds) return []

  const moduleIdsArray = moduleIds.split(',').map((id) => parseInt(id.trim()))

  const selectedModules = Object.entries(MODULE_PERMISSIONS)
    .filter(([, value]) => moduleIdsArray.includes(value))
    .map(([key]) => key)

  return selectedModules
}

export const getAttendanceStatusBadgeColor = (status: number) => {
  switch (status) {
    case ATTENDANCE_STATUS.STATUS_ABSENT:
      return 'badge-light-danger'
    case ATTENDANCE_STATUS.STATUS_LEAVE:
      return 'badge-light-info'
    case ATTENDANCE_STATUS.STATUS_LATE_PRESENT:
      return 'badge-light-warning'
    case ATTENDANCE_STATUS.STATUS_PRESENT:
      return 'badge-light-success'
    case ATTENDANCE_STATUS.STATUS_BUNK:
      return 'badge-light-danger'
    default:
      return 'badge-light-warning'
  }
}

export const getAttendanceCheckoutStatusBadgeColor = (status: number) => {
  switch (status) {
    case ATTENDANCE_STATUS.STATUS_ON_TIME_CHECKOUT:
      return 'badge-light-success'
    case ATTENDANCE_STATUS.STATUS_EARLY_CHECKOUT:
      return 'badge-light-danger'
    default:
      return 'badge-light-warning'
  }
}

export const getAttendancePercentageBadgeColor = (percentage: number, cutOffPercentage: number) => {
  if (percentage < cutOffPercentage) {
    return 'badge-light-danger'
  } else {
    return 'badge-light-success'
  }
}

export const getAttendanceStatusString = (
  status: number,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  campus_status?: number,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  status_bunk?: number
) => {
  const attendance_setting = store.getState()?.mainReducer?.attendance_setting[0]
  switch (status) {
    case ATTENDANCE_STATUS.STATUS_ABSENT:
      // The following condition was originally used to calculate bunk based on the `status_late_bunk` flag.
      // As per the recommendation by Saqib, we will now calculate "bunk" based on campus attendance status.
      // The original logic using `status_late_bunk` flag is commented out for clarity and future reference.

      // if (
      //   attendance_setting?.enable_bunk &&
      //   status_bunk &&
      //   status_bunk !== GENERAL_ENABLE_DISABLE.DISABLE
      // ) {
      //   return 'ATTENDANCE.ATTENDANCE_GRID.STATUS_BUNK'
      // }

      // Commented as per meeting with Saqib
      // if (
      //   attendance_setting?.enable_bunk &&
      //   campus_status &&
      //   [ATTENDANCE_STATUS.STATUS_PRESENT, ATTENDANCE_STATUS.STATUS_LATE_PRESENT].includes(
      //     campus_status
      //   )
      // ) {
      //   return 'ATTENDANCE.ATTENDANCE_GRID.STATUS_BUNK'
      // }
      return 'ATTENDANCE.ATTENDANCE_GRID.STATUS_ABSENT'
    case ATTENDANCE_STATUS.STATUS_LEAVE:
      return 'ATTENDANCE.ATTENDANCE_GRID.STATUS_LEAVE'
    case ATTENDANCE_STATUS.STATUS_LATE_PRESENT:
      return attendance_setting?.late_attendance_text
        ? attendance_setting?.late_attendance_text
        : 'ATTENDANCE.ATTENDANCE_GRID.STATUS_LATE'
    case ATTENDANCE_STATUS.STATUS_PRESENT:
      return 'ATTENDANCE.ATTENDANCE_GRID.STATUS_PRESENT'
    case ATTENDANCE_STATUS.STATUS_BUNK:
      return 'ATTENDANCE.ATTENDANCE_GRID.STATUS_BUNK'
    default:
      break
  }
}

export const AttendanceStatusLabel = () => {
  const attendance_setting = store.getState()?.mainReducer?.attendance_setting[0]
  return [
    {label: 'ATTENDANCE.ATTENDANCE_GRID.STATUS_ABSENT', value: 0},
    {label: 'ATTENDANCE.ATTENDANCE_GRID.STATUS_PRESENT', value: 1},
    {label: 'ATTENDANCE.ATTENDANCE_GRID.STATUS_LEAVE', value: 2},
    {
      label: attendance_setting?.late_attendance_text
        ? attendance_setting?.late_attendance_text
        : 'ATTENDANCE.ATTENDANCE_GRID.STATUS_LATE',
      value: 3,
    },
  ]
}

export const getAttendanceCheckoutStatusString = (status: number) => {
  switch (status) {
    case ATTENDANCE_STATUS.STATUS_EARLY_CHECKOUT:
      return 'ATTENDANCE.ATTENDANCE_GRID.CHECKOUT_EARLY_CHECKOUT'
    case ATTENDANCE_STATUS.STATUS_ON_TIME_CHECKOUT:
      return 'ATTENDANCE.ATTENDANCE_GRID.CHECKOUT_ON_TIME_CHECKOUT'
    default:
      return 'ATTENDANCE.ATTENDANCE_GRID.UNKNOWN_STATUS'
  }
}

export const getLoginStatusBadgeColor = (status: string | number) =>
  status === 1 ? 'badge-success' : 'badge-danger'

export const getLoginStatusString = (status: number) =>
  status === 1 ? 'GENERAL.YES' : 'GENERAL.NO'

export const getNewsStatusBadgeColor = (status: number) =>
  status === 0 ? 'badge-warning' : 'badge-success'

export const getNewsPublishedString = (status: number) =>
  status === 0 ? 'NEWS.SCHEDULED' : 'NEWS.PUBLISHED'

export const getFromTemplateString = (status: number) =>
  status === 1 ? 'GENERAL.YES' : 'GENERAL.NO'

export const isClasswiseInstance = () => {
  const reduxStore = store.getState()
  const {generalSettings} = reduxStore.mainReducer
  return generalSettings.subject_settings_permission === CLASS_WISE_INSTANCE
}

export const isSubjectwiseInstance = () => {
  const reduxStore = store.getState()
  const {generalSettings} = reduxStore.mainReducer
  return generalSettings.subject_settings_permission === SUBJECT_WISE_INSTANCE
}

export const getCurrentAndPreviousFourYears = () => {
  const currentYear = moment().year()
  const years = []
  for (let i = 0; i < 5; i++) {
    const year = currentYear - i
    years.push({label: year, value: year})
  }
  return years
}

export const getAllMonths = () => {
  const months = []
  for (let i = 0; i < 12; i++) {
    months.push({label: moment().month(i).format('MMMM'), value: i})
  }
  return months
}

export const isDevEnv = (): boolean => {
  return import.meta.env.VITE_APP_ENV !== 'prod'
}

/**
 * The gate on the NEW exam manager (the React/Node conversion), which runs in
 * PARALLEL with the legacy iframe module (Saqib, 2026-08-20).
 *
 * True when either:
 *  - this is a dev build (`isDevEnv()`), so dev keeps seeing everything, or
 *  - the tenant opted in via `general_settings.new_exam_manager` AND the
 *    super admin's exam feature is on. The feature check rides inside
 *    `hasPermission('exam_manager')`: getUserPermissions grants that module
 *    key only when `app_module_permissions.exam_section` is 1, so the flag
 *    alone can never expose the module to a tenant whose exam feature is off.
 *
 * Every exam-module surface that shipped behind `isDevEnv()` now gates on
 * this instead. The legacy iframe module stays untouched either way — the
 * beta sidebar group ADDS the new module beside it, never replaces it.
 *
 * NON-REACTIVE — the store is read imperatively, so the result is whatever
 * the store held at call time. Fine for code that re-runs per render off a
 * subscribed parent (SidebarLinks via SidebarMenuMain) or after boot
 * (List.tsx, RecheckingForm). Route tables must NOT call this: on a prod
 * hard-load they render before GET /general-settings resolves and would
 * stick with the flag read as off — use the
 * `useIsNewExamManagerEnabled` hook (common/hooks) instead.
 */
export const isNewExamManagerEnabled = (): boolean => {
  if (isDevEnv()) return true
  const {generalSettings} = store.getState().mainReducer
  return Number(generalSettings?.new_exam_manager) === 1 && hasPermission('exam_manager')
}

export const isProdEnv = (): boolean => {
  return import.meta.env.VITE_APP_ENV === 'prod'
}

export const isAIEnvironment = (): boolean => {
  return import.meta.env.VITE_APP_ENV === 'ai'
}

export const isAIDomain = (): boolean => {
  return window.location.hostname.includes('.ai')
}

export const isComDomain = (): boolean => {
  return window.location.hostname.includes('.com')
}

export const isOnlineDomain = (): boolean => {
  return window.location.hostname.includes('.online')
}

export const isInfoDomain = (): boolean => {
  return window.location.hostname.includes('.info')
}

/**
 * Design Tool module is available on every domain/tenant — visibility is controlled
 * by the 'design_tool_view' permission (a staff role without View gets no sidebar
 * link or route, even if it carries create/update/delete).
 */
export const isDesignToolEnabled = (): boolean => {
  return hasPermission('design_tool') && hasPermission('design_tool_view')
}

/** Tenants allowed to use the attendance chatbot on the .online domain. */
// const ATTENDANCE_CHATBOT_TENANTS = ['tres', 'democollege', 'verge', 'whales', 'rayden', 'linksedu']

/**
 * Attendance chatbot: open to every Ilmversity tenant except LGS; hidden on the selective AI (Da1Timetable) domains.
 * The previous tenant allowlist is kept commented out below for reference.
 */
export const isAttendanceChatbotEnabled = (): boolean => {
  // const hostname = window.location.hostname
  // if (isInfoDomain() || hostname.includes('localhost')) return true
  // return isOnlineDomain() || ATTENDANCE_CHATBOT_TENANTS.includes(getTenantName())
  return !isSelectiveAiDomains() && !isLgs()
}

/**
 * FAQ/support chatbot gating — the same `knowledge_base` permission that already
 * controls the /user-manual sidebar link (backfilled to 1 for all tenants). Disabled for LGS.
 */
export const isFaqChatbotEnabled = (): boolean => !isLgs() && hasPermission('knowledge_base')

export const getMonthsForPreviousCurrentAndNextYear = () => {
  const currentYear = moment().year()
  const years = [currentYear - 1, currentYear, currentYear + 1]

  const monthsForYears = years.flatMap((year) =>
    moment.months().map((month, index) => ({
      label: moment().year(year).month(index).format('MMM YYYY'),
      value: moment().year(year).month(index).format('YYYY-MM'),
    }))
  )
  return monthsForYears
}

export const getDirtyValues = (initialValues: any, currentValues: any) => {
  if (Array.isArray(initialValues) && Array.isArray(currentValues)) {
    return currentValues.filter((value, index) => !_.isEqual(value, initialValues[index]))
  } else if (_.isPlainObject(initialValues) && _.isPlainObject(currentValues)) {
    return _.pickBy(currentValues, (value, key) => !_.isEqual(value, initialValues[key]))
  }
  return currentValues
}

export const getGenderName = (gender: number, intl: IntlShape): string => {
  switch (gender) {
    case GENDER.MALE:
      return intl.$t({id: 'GENDER.MALE'})
    case GENDER.FEMALE:
      return intl.$t({id: 'GENDER.FEMALE'})
    default:
      return intl.$t({id: 'GENDER.MALE'})
  }
}

export const convertTo12HourFormat = (time24: any) => {
  if (!time24 || typeof time24 !== 'string') {
    return '' // Return an empty string for null, or invalid input
  }

  const [hours, minutes] = time24.split(':')

  if (!hours || !minutes || isNaN(parseInt(hours)) || isNaN(parseInt(minutes))) {
    return '' // Return an empty string for invalid time formats
  }

  let hours12 = parseInt(hours, 10)
  const period = hours12 >= 12 ? 'PM' : 'AM'
  hours12 = hours12 % 12 || 12

  return `${hours12}:${minutes} ${period}`
}

export const convertTo24HourFormat = (time: any): string => {
  if (!time || typeof time !== 'string') return ''
  const parsed = moment(
    time.trim(),
    ['h:mm A', 'hh:mm A', 'h:mmA', 'hh:mmA', 'H:mm', 'HH:mm', 'H:mm:ss', 'HH:mm:ss'],
    false
  )
  if (!parsed.isValid()) return ''
  return parsed.format('HH:mm')
}
export const formattedTime = (date: any) => {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}:00`
}

export const customFormatDate = (date: any) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const getSubjectStatusString = (status: number) => {
  switch (status) {
    case SUBJECTS_STATUS.ACTIVE:
      return 'GENERAL.ACTIVE'
    case SUBJECTS_STATUS.INACTIVE:
      return 'GENERAL.INACTIVE'
    default:
      return 'GENERAL.PENDING'
  }
}

export const getGradesList = (grades: any[]): Option[] => {
  const branch = Number(localStorage.getItem('branch'))
  let filteredGrades: any
  if (branch !== null && branch !== GENERAL.INACTIVE) {
    filteredGrades =
      grades &&
      grades
        .filter((item: any) => item.status === 1 && item.branchId === branch)
        .map((item: any) => ({
          label: `${item?.name} (${item?.levelName})`,
          value: item?.id,
          select: true,
        }))
  } else {
    filteredGrades =
      grades &&
      grades
        .filter((item: any) => item.status === 1)
        .map((item: any) => ({
          label: `${item?.name} (${item?.levelName})`,
          value: item?.id,
          select: true,
        }))
  }

  return filteredGrades || []
}

export const getSubjectClassesList = (gradeId: string | null = null): Option[] => {
  const reduxStore = store.getState()
  const {classes} = reduxStore.mainReducer

  const filteredClasses = classes
    ?.filter((item: any) => item.status === 1 && item.gradeId == gradeId)
    .map((item: any) => ({
      label: `${item?.name} ${item.branch_name}`,
      value: item?.id,
    }))
  return filteredClasses || []
}

export const calculateAge = (dob: string) => {
  const dateOnly = getDateOnlyString(dob)
  const birthDate = moment(dateOnly, 'YYYY-MM-DD', true)
  if (!birthDate.isValid()) return 'Not Set'

  const today = new Date()

  let years = today.getFullYear() - birthDate.year()
  let months = today.getMonth() - birthDate.month()

  if (months < 0 || (months === 0 && today.getDate() < birthDate.date())) {
    years--
    months = (months + 12) % 12
  }

  return `${years} Years ${months} Months`
}

export const getClassSubejctStatusString = (status: number) => {
  switch (status) {
    case CLASSES_SUBJECT_STATUS.YES:
      return 'GENERAL.YES'
    case CLASSES_SUBJECT_STATUS.NO:
      return 'GENERAL.NO'
    default:
      return 'GENERAL.PENDING'
  }
}

export const getHomeworkResultString = (status: number) => {
  switch (status) {
    case HOMEWORK_RESULTS.PARTIAL:
      return 'HOMEWORK_RESULTS.PARTIAL'
    case HOMEWORK_RESULTS.COMPLETE:
      return 'HOMEWORK_RESULTS.COMPLETE'
    case HOMEWORK_RESULTS.NONE:
      return 'HOMEWORK_RESULTS.NONE'
    default:
      return 'GENERAL.PENDING'
  }
}

export const getTestResultString = (status: number) => {
  switch (status) {
    case TEST_RESULTS.PARTIAL:
      return 'TEST_RESULTS.PARTIAL'
    case TEST_RESULTS.COMPLETE:
      return 'TEST_RESULTS.COMPLETE'
    case TEST_RESULTS.NONE:
      return 'TEST_RESULTS.NONE'
    default:
      return 'GENERAL.PENDING'
  }
}

export const getInvoiceTypeString = (status: number) => {
  switch (status) {
    case INVOICE_TYPE.GENERATED:
      return 'ROYALTY.FEE_GENERATED'
    case INVOICE_TYPE.RECEIVED:
      return 'ROYALTY.FEE_RECEIVED'
    case INVOICE_TYPE.COLLECTION:
      return 'ROYALTY.FEE_COLLECTION'
  }
}

export const getInvoiceStatus = (status: number) => {
  switch (status) {
    case INVOICE_STATUS.PAID:
      return 'ROYALTY.INVOICE_PAID'
    case INVOICE_STATUS.UNPAID:
      return 'ROYALTY.INVOICE_DUE'
    case INVOICE_STATUS.PARTIAL:
      return 'ROYALTY.INVOICE_PARTIAL'
  }
}

export const getQuestionTypeString = (status: any) => {
  switch (status) {
    case QUESTION_TYPES.MULTIPLE_CHOICE:
      return 'QUESTION_TYPES.MULTIPLE_CHOICE'
    case QUESTION_TYPES.DROPDOWN_ANSWER:
      return 'QUESTION_TYPES.DROPDOWN_ANSWER'
    case QUESTION_TYPES.SHORT_TEXT_ANSWER:
      return 'QUESTION_TYPES.SHORT_TEXT_ANSWER'
    case QUESTION_TYPES.LONG_TEXT_ANSWER:
      return 'QUESTION_TYPES.LONG_TEXT_ANSWER'
    case QUESTION_TYPES.FILL_IN_THE_BLANK:
      return 'QUESTION_TYPES.FILL_IN_THE_BLANK'
    case QUESTION_TYPES.TRUE_FALSE:
      return 'QUESTION_TYPES.TRUE_FALSE'
    default:
      return 'HOMEWORK.NOT_APPLICABLE'
  }
}

/**
 * Get an array of dates between two specified dates.
 * @param {string} startDate - The starting date in 'YYYY-MM-DD' format.
 * @param {string} endDate - The ending date in 'YYYY-MM-DD' format.
 * @returns {string[]} An array of date strings in 'YYYY-MM-DD' format.
 */
export const getDatesBetween = (startDate: string, endDate: string) => {
  const start = moment(startDate)
  const end = moment(endDate)
  const dates = []
  while (start.isBefore(end) || start.isSame(end)) {
    dates.push(start.format('YYYY-MM-DD'))
    start.add(1, 'days')
  }
  return dates
}

export const getBranchSubjectClassesList = (branchId: string | null = null): Option[] => {
  const reduxStore = store.getState()
  const {classes} = reduxStore.mainReducer

  const filteredClasses = classes
    ?.filter((item: any) => item.status === 1 && item.branch_id === branchId)
    .map((item: any) => ({
      label: `${item?.name} ${item.branch_name}`,
      value: item?.id,
    }))
  return filteredClasses || []
}

export const getRegistrationStatus = (status: number, intl: IntlShape) => {
  switch (status) {
    case REGISTRATION_STATUS.REGISTERED_AND_ADMITTED:
      return intl.$t({id: 'STUDENT.REGISTERED_AND_ADMITTED'})
    case REGISTRATION_STATUS.ONLY_REGISTERED:
      return intl.$t({id: 'STUDENT.ONLY_REGISTERED'})
    default:
      return intl.$t({id: 'STUDENT.REGISTERED_AND_ADMITTED'})
  }
}

export const getViewStatus = (status: number, intl: IntlShape) => {
  switch (status) {
    case GRADE_STATUS.INACTIVE:
      return intl.$t({id: 'GENERAL.YES'})
    case GRADE_STATUS.ACTIVE:
      return intl.$t({id: 'GENERAL.NO'})
    default:
      return intl.$t({id: 'CLASS.NOT_SET'})
  }
}

export const getMaritalStatusLabel = (status: number, intl: IntlShape) => {
  switch (status) {
    case TEACHER_MARITAL_STATUS.MARRIED:
      return intl.formatMessage({id: 'MARITAL.MARRIED'})
    case TEACHER_MARITAL_STATUS.SEPARATED:
      return intl.formatMessage({id: 'MARITAL.SEPARATED'})
    case TEACHER_MARITAL_STATUS.DIVORCED:
      return intl.formatMessage({id: 'MARITAL.DIVORCED'})
    case TEACHER_MARITAL_STATUS.WIDOW:
      return intl.formatMessage({id: 'MARITAL.WIDOW'})
    case TEACHER_MARITAL_STATUS.SINGLE:
      return intl.formatMessage({id: 'MARITAL.SINGLE'})
    default:
      return intl.formatMessage({id: 'CLASS.NOT_SET'})
  }
}

export const isDemocollege = () => {
  return ['democollege'].includes(getTenantName())
}

export const isLgs = () => {
  return ['lgs', 'lgsdev2'].includes(getTenantName())
}

export const isLyceum = () => {
  return ['lyceum'].includes(getTenantName())
}

export const isMyschool = () => {
  return ['myschool'].includes(getTenantName())
}

export const isLyceumTest = () => {
  return ['lyceumtest'].includes(getTenantName())
}

export const isRisc = () => {
  return ['risc'].includes(getTenantName())
}

export const isTres = () => {
  return ['tres'].includes(getTenantName())
}
export const isNimchs = () => {
  return ['nimchs'].includes(getTenantName())
}
export const isAIDevEnv = () => {
  return ['aidemo', 'ai1', 'ai2', 'ai3', 'ai4'].includes(getTenantName())
}

export const isEureka = () => {
  return ['eureka'].includes(getTenantName())
}
export const isSelectiveAiDomains = () => {
  return (
    //window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('da1timetable.ai') ||
    window.location.hostname.includes('da1timetable.info') ||
    window.location.hostname.includes('ilmversity.ai')
  )

  // return ['ai4'].includes(getTenantName())
}

/**
 * Returns the central Google auth domain for the current root domain.
 * e.g. auth.da1timetable.info, auth.da1timetable.ai, auth.ilmversity.ai
 * On localhost, uses VITE_GOOGLE_AUTH_DOMAIN env variable.
 */
export const getGoogleAuthDomain = (): string => {
  const envDomain = import.meta.env.VITE_GOOGLE_AUTH_DOMAIN
  if (envDomain) return envDomain
  const hostname = window.location.hostname
  if (hostname.includes('da1timetable.info')) return 'auth.da1timetable.info'
  if (hostname.includes('da1timetable.ai')) return 'auth.da1timetable.ai'
  if (hostname.includes('ilmversity.ai')) return 'auth.ilmversity.ai'
  return ''
}

/**
 * Returns the full URL prefix for the Google auth domain (with correct protocol).
 */
export const getGoogleAuthUrl = (): string => {
  const domain = getGoogleAuthDomain()
  if (!domain) return ''
  const protocol = domain.includes('localhost') ? 'http' : 'https'
  return `${protocol}://${domain}`
}

/** Subscription sign-up URL for universal login (dev vs production). */
export const getSubscriptionSignUpUrl = (): string => {
  const hostname = window.location.hostname
  const isDev = hostname.includes('localhost') || hostname.includes('da1timetable.info')
  return isDev
    ? 'https://dev-subscription.da1timetable.ai/signup/subscriptions/choose'
    : 'https://subscription.da1timetable.ai/signup/subscriptions/choose'
}

/**
 * Whether the current hostname is the central Google auth domain (auth.*)
 * On localhost this is always false — the GoogleAuthPage component handles it via routing.
 */
export const isGoogleAuthDomain = (): boolean => {
  const hostname = window.location.hostname
  return (
    hostname === 'auth.da1timetable.info' ||
    hostname === 'auth.da1timetable.ai' ||
    hostname === 'auth.ilmversity.ai'
  )
}

/**
 * Allowed root domains for returnTo validation (prevents open redirect)
 */
export const isAllowedReturnDomain = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    const host = parsed.hostname
    return (
      host.endsWith('.da1timetable.info') ||
      host.endsWith('.da1timetable.ai') ||
      host.endsWith('.ilmversity.ai') ||
      host.includes('localhost')
    )
  } catch {
    return false
  }
}

// Helper Function for local host
export const isLocalHost = () => {
  return window.location.hostname.includes('localhost')
}

export const confirmDiscard = async () => {
  const result = await Swal.fire({
    title: 'Pending Changes',
    text: 'You have some pending changes. Do you want to discard them?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, discard!',
    cancelButtonText: 'No, stay here',
  })
  return result.isConfirmed
}
export const isLyceumDevEnv = () => {
  return !isLyceum() || isDevEnv()
}

export const isConcordia = () => {
  return ['concordia'].includes(getTenantName())
}

export const isLinksEdu = () => {
  return getTenantName() === 'linksedu'
}

export const convertS3Url = (originalUrl: string) => {
  if (!originalUrl.includes('ilmversity-uploads-bkp')) {
    const newUrl = originalUrl.replace('ilmversity-uploads', 'ilmversity-uploads-bkp')
    return newUrl
  }
  return originalUrl
}

export const getStudentListGrades = (select = true, level_name = true): Option[] => {
  const reduxStore = store.getState()
  const {grades} = reduxStore.mainReducer
  const branch = localStorage.getItem('branch')
  let filteredGrades = []

  if (branch === null) {
    filteredGrades =
      grades &&
      grades
        .filter((item: any) => item.status === 1)
        .map((item: any) => ({
          label: `${item?.name} ${level_name ? item?.levelName : ''}`,
          value: item?.id,
          select: select ? true : false,
        }))
  } else {
    filteredGrades =
      grades &&
      grades
        .filter((item: any) => item.status === 1 && item.branchId == branch)
        .map((item: any) => ({
          label: `${item?.name} ${level_name ? item?.levelName : ''}`,
          value: item?.id,
          select: select ? true : false,
        }))
  }

  return filteredGrades || []
}

export const SUPPORTED_FILE_TYPES = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.rtf',
  '.xls',
  '.xlsx',
  '.csv',
  '.png',
  '.jpg',
  '.jpeg',
]

type FileUploadParams = {
  dispatch: any
  path: string
  file: File
  uploadPrefix?: string
  existingDocId?: number
  /** Called with the stored URL of the uploaded file. The return value is the
   *  document id only, and a caller that displays the file straight after
   *  uploading needs the stored URL — a local `blob:` is not reachable by the
   *  hosted PDF/Office viewers, nor by a WebView download. */
  onUrl?: (fileUrl: string) => void
}

export const uploadFile = async ({
  dispatch,
  path,
  file,
  uploadPrefix = `uploads/${getTenantDomain().replaceAll('.', '_')}`,
  existingDocId,
  onUrl,
}: FileUploadParams): Promise<number> => {
  const DOCUMENT_UPLOAD_PATH = `${uploadPrefix}${path}`

  const payload = {
    path: DOCUMENT_UPLOAD_PATH,
    contentType: file.type,
    fileName: file.name,
  }

  try {
    const res: any = await new Promise((resolve, reject) =>
      dispatch(getSignedUploadUrl(payload, resolve, reject))
    )

    const {fileUrl, uploadURL} = res.result
    onUrl?.(fileUrl)
    const buffer = await file.arrayBuffer()

    await putToSignedUrl(uploadURL, buffer, file.type)

    const confirmBody = {
      path: DOCUMENT_UPLOAD_PATH,
      original_filename: file.name,
      file_name: fileUrl.split('/').pop(),
      url: fileUrl,
      contentType: file.type,
      size: file.size,
    }

    const confirmRes: any = await new Promise((resolve, reject) =>
      dispatch(confirmFileUpload(confirmBody, resolve, reject))
    )

    // 18/12/2024:- delete document api will only work if document table is not referenced by a foreign key constraint in your current doc table
    if (existingDocId && confirmRes?.result?.insertId) {
      dispatch(deleteDocument(existingDocId))
    }
    return confirmRes?.result?.insertId
  } catch (error: any) {
    return error?.message
  }
}

export const maskEmail = (email: string) => {
  const [localPart, domain] = email.split('@')
  if (localPart.length <= 2) {
    return localPart[0] + '*'.repeat(localPart.length - 1) + '@' + domain
  }

  const maskedPart = '*'.repeat(localPart.length - 2)
  return localPart[0] + maskedPart + localPart[localPart.length - 1] + '@' + domain
}

export const getBaseUrl = () => constants.api_base_url.replace(/\/api\/?$/, '')

export const getLineSpaces = (): Option[] => {
  const spaces: Option[] = []
  for (let i = 6; i <= 40; i++) {
    spaces.push({
      label: i.toString(),
      value: i,
    })
  }
  return spaces
}

export const replaceEnrollmentCertificateVariables = (
  content: string,
  student: any,
  parent: any
) => {
  const stuName = `${student?.firstName || ''} ${student?.lastName || ''}`.trim()
  const regDate = student?.registrationDate || ''

  const parentName = `${parent?.firstName || ''} ${parent?.lastName || ''}`.trim()
  const parentCnic = parent?.cnic || ''

  const fatherName = parent?.father_name || ''
  const fatherCnic = parent?.father_cnic || ''

  const motherName = parent?.mother_name || ''
  const motherCnic = parent?.mother_cnic || ''

  const dob = student?.dob || ''
  const address = student?.address || ''
  const grade = student?.class || ''

  const currDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  content = content
    .replace(/\[studentName\]/g, stuName)
    .replace(/\[fathername\]/g, fatherName)
    .replace(/\[f_cnic\]/g, fatherCnic)
    .replace(/\[parentname\]/g, parentName)
    .replace(/\[parent_cnic\]/g, parentCnic)
    .replace(/\[regDate\]/g, regDate)
    .replace(/\[dob\]/g, dob)
    .replace(/\[mothername\]/g, motherName)
    .replace(/\[m_cnic\]/g, motherCnic)
    .replace(/\[address\]/g, address)
    .replace(/\[grade\]/g, grade)
    .replace(/\[currdate\]/g, currDate)

  return content
}

export const currentUser = (): UserModel => store.getState().userReducer.user

/**
 * Parse features string from general settings API response
 * @param featuresString - The features string from API response
 * @returns Parsed features object or null if parsing fails
 */
export const parseFeatures = (featuresString: string) => {
  try {
    // If it's already a valid JSON string, parse directly
    if (featuresString.trim().startsWith('{') && featuresString.trim().endsWith('}')) {
      try {
        return JSON.parse(featuresString)
      } catch {
        // If direct parsing fails, try cleaning
      }
    }

    // Clean up the string to make it valid JSON
    // First remove all newlines and extra spaces, then handle JavaScript object syntax
    let cleanedString = featuresString
      .replace(/\n/g, ' ') // Remove newlines
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim() // Remove leading/trailing spaces

    // Handle JavaScript object syntax step by step
    // 1. Handle property names without quotes (but be careful with values)
    cleanedString = cleanedString.replace(/(\w+)(\s*):/g, '"$1"$2:')

    // 2. Replace single quotes with double quotes for strings
    cleanedString = cleanedString.replace(/'([^']*)'/g, '"$1"')

    const parsed = JSON.parse(cleanedString)
    return parsed
  } catch {
    // Try a more aggressive approach as fallback
    try {
      const fallbackString = featuresString
        .replace(/\n/g, '')
        .replace(/\s+/g, ' ')
        .replace(/(\w+):/g, '"$1":')
        .replace(/'/g, '"')
        .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas

      const fallbackParsed = JSON.parse(fallbackString)
      return fallbackParsed
    } catch {
      return null
    }
  }
}

/**
 * Check if features environment matches current tenant environment
 * @param features - Parsed features object
 * @returns boolean indicating if environments match
 */
export const isFeatureEnvironmentValid = (features: any): boolean => {
  if (!features || !features.env) return false

  const currentEnv = getAppEnv()
  return features.env === currentEnv
}

/**
 * Check if class module is enabled and configured properly
 * @param features - Parsed features object
 * @returns Object with enablement status and configuration
 */
export const getClassModuleConfig = (features: any) => {
  const defaultConfig = {
    enabled: undefined, // undefined means not configured
    features: {},
    isValid: false,
  }

  if (!features || !features.modules || !features.modules.class_module) {
    return defaultConfig
  }

  const classModule = features.modules.class_module

  return {
    enabled: classModule.enabled, // Keep the actual value (true/false/undefined)
    features: classModule.features || {},
    isValid: classModule.enabled !== undefined, // Valid if explicitly set to true or false
  }
}

/**
 * Get add class form configuration based on features
 * @param features - Parsed features object
 * @returns Object with form configuration for add class
 */
export const getAddClassFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }

  const classModuleConfig = getClassModuleConfig(features)

  if (!classModuleConfig.isValid || !classModuleConfig.features.add_class) {
    return defaultConfig
  }

  const addClassFeature = classModuleConfig.features.add_class
  const mode = addClassFeature.mode || 'default'

  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: addClassFeature.columns || [],
    mode: mode,
    isMiniMode: mode === 'mini',
  }
}

/**
 * Get update class form configuration based on features
 * @param features - Parsed features object
 * @returns Object with form configuration for update class
 */
export const getUpdateClassFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }

  const classModuleConfig = getClassModuleConfig(features)

  if (!classModuleConfig.isValid || !classModuleConfig.features.update_class) {
    return defaultConfig
  }

  const updateClassFeature = classModuleConfig.features.update_class
  const mode = updateClassFeature.mode || 'default'

  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: updateClassFeature.columns || [],
    mode: mode,
    isMiniMode: mode === 'mini',
  }
}

// Subject module feature helpers
export const getSubjectModuleConfig = (features: any) => {
  const defaultConfig = {enabled: undefined, features: {}, isValid: false}
  if (!features || !features.modules || !features.modules.subject_module) {
    return defaultConfig
  }
  const subjectModule = features.modules.subject_module
  return {
    enabled: subjectModule.enabled,
    features: subjectModule.features || {},
    isValid: subjectModule.enabled !== undefined,
  }
}

// Student module feature helpers
export const getStudentModuleConfig = (features: any) => {
  const defaultConfig = {enabled: undefined, features: {}, isValid: false}
  if (!features || !features.modules || !features.modules.student_module) {
    return defaultConfig
  }
  const studentModule = features.modules.student_module
  return {
    enabled: studentModule.enabled,
    features: studentModule.features || {},
    isValid: studentModule.enabled !== undefined,
  }
}

export const getAddStudentFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const studentModuleConfig = getStudentModuleConfig(features)
  if (!studentModuleConfig.isValid || !studentModuleConfig.features.add_student)
    return defaultConfig
  const feature = studentModuleConfig.features.add_student
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

export const getUpdateStudentFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const studentModuleConfig = getStudentModuleConfig(features)
  if (!studentModuleConfig.isValid || !studentModuleConfig.features.update_student)
    return defaultConfig
  const feature = studentModuleConfig.features.update_student
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

// Teacher module feature helpers
export const getTeacherModuleConfig = (features: any) => {
  const defaultConfig = {enabled: undefined, features: {}, isValid: false}
  if (!features || !features.modules || !features.modules.teacher_module) {
    return defaultConfig
  }
  const teacherModule = features.modules.teacher_module
  return {
    enabled: teacherModule.enabled,
    features: teacherModule.features || {},
    isValid: teacherModule.enabled !== undefined,
  }
}

// Staff module feature helpers
export const getStaffModuleConfig = (features: any) => {
  const defaultConfig = {enabled: undefined, features: {}, isValid: false}
  if (!features || !features.modules || !features.modules.staff_module) {
    return defaultConfig
  }
  const staffModule = features.modules.staff_module
  return {
    enabled: staffModule.enabled,
    features: staffModule.features || {},
    isValid: staffModule.enabled !== undefined,
  }
}

export const getAddStaffFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const staffModuleConfig = getStaffModuleConfig(features)
  if (!staffModuleConfig.isValid || !staffModuleConfig.features.add_staff) return defaultConfig
  const feature = staffModuleConfig.features.add_staff
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

export const getUpdateStaffFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const staffModuleConfig = getStaffModuleConfig(features)
  if (!staffModuleConfig.isValid || !staffModuleConfig.features.update_staff) return defaultConfig
  const feature = staffModuleConfig.features.update_staff
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

export const getAddTeacherFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const teacherModuleConfig = getTeacherModuleConfig(features)
  if (!teacherModuleConfig.isValid || !teacherModuleConfig.features.add_teacher)
    return defaultConfig
  const feature = teacherModuleConfig.features.add_teacher
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

export const getUpdateTeacherFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const teacherModuleConfig = getTeacherModuleConfig(features)
  if (!teacherModuleConfig.isValid || !teacherModuleConfig.features.update_teacher)
    return defaultConfig
  const feature = teacherModuleConfig.features.update_teacher
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

export const getAddSubjectFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const subjectModuleConfig = getSubjectModuleConfig(features)
  if (!subjectModuleConfig.isValid || !subjectModuleConfig.features.add_subject)
    return defaultConfig
  const feature = subjectModuleConfig.features.add_subject
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

export const getUpdateSubjectFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const subjectModuleConfig = getSubjectModuleConfig(features)
  if (!subjectModuleConfig.isValid || !subjectModuleConfig.features.update_subject)
    return defaultConfig
  const feature = subjectModuleConfig.features.update_subject
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

// Grade module feature helpers
export const getGradeModuleConfig = (features: any) => {
  const defaultConfig = {enabled: undefined, features: {}, isValid: false}
  if (!features || !features.modules || !features.modules.grades_module) {
    return defaultConfig
  }
  const gradesModule = features.modules.grades_module
  return {
    enabled: gradesModule.enabled,
    features: gradesModule.features || {},
    isValid: gradesModule.enabled !== undefined,
  }
}

export const getAddGradeFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const gradesModuleConfig = getGradeModuleConfig(features)
  if (!gradesModuleConfig.isValid || !gradesModuleConfig.features.add_grade) return defaultConfig
  const feature = gradesModuleConfig.features.add_grade
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

export const getUpdateGradeFormConfig = (features: any) => {
  const defaultConfig = {
    shouldShowCustomForm: false,
    allowedColumns: [],
    mode: 'default',
    isMiniMode: false,
  }
  const gradesModuleConfig = getGradeModuleConfig(features)
  if (!gradesModuleConfig.isValid || !gradesModuleConfig.features.update_grade) return defaultConfig
  const feature = gradesModuleConfig.features.update_grade
  const mode = feature.mode || 'default'
  return {
    shouldShowCustomForm: mode === 'custom',
    allowedColumns: feature.columns || [],
    mode,
    isMiniMode: mode === 'mini',
  }
}

/**
 * Main function to process features from general settings API
 * @param featuresString - The features string from API response
 * @returns Complete features configuration object
 */
export const processGeneralSettingsFeatures = (featuresString: string) => {
  const features = parseFeatures(featuresString)

  if (!features) {
    return {
      isValid: false,
      environmentMatch: false,
      classModule: {
        enabled: false,
        features: {},
        isValid: false,
      },
      addClassForm: {
        shouldShowCustomForm: false,
        allowedColumns: [],
        mode: 'default',
        isMiniMode: false,
      },
      updateClassForm: {
        shouldShowCustomForm: false,
        allowedColumns: [],
        mode: 'default',
        isMiniMode: false,
      },
    }
  }

  const environmentMatch = isFeatureEnvironmentValid(features)

  if (!environmentMatch) {
    return {
      isValid: false,
      environmentMatch: false,
      classModule: {
        enabled: false,
        features: {},
        isValid: false,
      },
      addClassForm: {
        shouldShowCustomForm: false,
        allowedColumns: [],
        mode: 'default',
        isMiniMode: false,
      },
      updateClassForm: {
        shouldShowCustomForm: false,
        allowedColumns: [],
        mode: 'default',
        isMiniMode: false,
      },
    }
  }

  const classModuleConfig = getClassModuleConfig(features)
  const addClassFormConfig = getAddClassFormConfig(features)
  const updateClassFormConfig = getUpdateClassFormConfig(features)
  const subjectModuleConfig = getSubjectModuleConfig(features)
  const addSubjectFormConfig = getAddSubjectFormConfig(features)
  const updateSubjectFormConfig = getUpdateSubjectFormConfig(features)
  const gradesModuleConfig = getGradeModuleConfig(features)
  const addGradeFormConfig = getAddGradeFormConfig(features)
  const updateGradeFormConfig = getUpdateGradeFormConfig(features)
  const studentModuleConfig = getStudentModuleConfig(features)
  const addStudentFormConfig = getAddStudentFormConfig(features)
  const updateStudentFormConfig = getUpdateStudentFormConfig(features)
  const teacherModuleConfig = getTeacherModuleConfig(features)
  const addTeacherFormConfig = getAddTeacherFormConfig(features)
  const updateTeacherFormConfig = getUpdateTeacherFormConfig(features)
  const staffModuleConfig = getStaffModuleConfig(features)
  const addStaffFormConfig = getAddStaffFormConfig(features)
  const updateStaffFormConfig = getUpdateStaffFormConfig(features)

  return {
    isValid: true,
    environmentMatch: true,
    classModule: classModuleConfig,
    addClassForm: addClassFormConfig,
    updateClassForm: updateClassFormConfig,
    subjectModule: subjectModuleConfig,
    addSubjectForm: addSubjectFormConfig,
    updateSubjectForm: updateSubjectFormConfig,
    gradesModule: gradesModuleConfig,
    addGradeForm: addGradeFormConfig,
    updateGradeForm: updateGradeFormConfig,
    studentModule: studentModuleConfig,
    addStudentForm: addStudentFormConfig,
    updateStudentForm: updateStudentFormConfig,
    teacherModule: teacherModuleConfig,
    addTeacherForm: addTeacherFormConfig,
    updateTeacherForm: updateTeacherFormConfig,
    staffModule: staffModuleConfig,
    addStaffForm: addStaffFormConfig,
    updateStaffForm: updateStaffFormConfig,
    rawFeatures: features,
  }
}

/**
 * Check if a specific column should be shown in add class form
 * @param column - Column name to check
 * @param featuresString - Features string from general settings
 * @returns boolean indicating if column should be shown
 */
export const shouldShowAddClassColumn = (column: string, featuresString?: string): boolean => {
  // Step 1: If no features configured, show all columns (fallback)
  if (!featuresString) {
    return true
  }

  // Step 2: Parse the features string
  const features = parseFeatures(featuresString)
  if (!features) {
    return true
  }

  // Step 3: Check environment - must match current env ('ai')
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) {
    return true // Show all if environment doesn't match
  }

  // Step 4: Check if modules exist
  if (!features.modules) {
    return true
  }

  // Step 5: Check if class_module exists
  if (!features.modules.class_module) {
    return true
  }

  const classModule = features.modules.class_module

  // Step 6: Check if module is enabled (must be true)
  if (classModule.enabled !== true) {
    return classModule.enabled === false ? false : true // false = hidden, undefined/null = show all
  }

  // Step 7: Check if features exist in class_module
  if (!classModule.features) {
    return true
  }

  // Step 8: Check if add_class feature exists
  if (!classModule.features.add_class) {
    return true
  }

  const addClassFeature = classModule.features.add_class

  // Step 9: Check mode - handle both 'custom' and 'mini'
  if (addClassFeature.mode === 'custom') {
    // Step 10: For custom mode, check if columns array exists and is valid
    if (!addClassFeature.columns || !Array.isArray(addClassFeature.columns)) {
      return true
    }
    // Step 11: Check if the specific column is in the allowed list
    return addClassFeature.columns.includes(column)
  } else if (addClassFeature.mode === 'mini') {
    // For mini mode, show main form fields but hide additional fields
    const hiddenFieldsInMini = ['label_id', 'color', 'description', 'capacity', 'room']
    return !hiddenFieldsInMini.includes(column)
  } else {
    // For any other mode or no mode specified, show all fields
    return true
  }
}

/**
 * Get filtered form fields for add class based on features configuration
 * @param allFields - Array of all possible field names
 * @param featuresString - Features string from general settings
 * @returns Array of allowed field names
 */
export const getFilteredAddClassFields = (
  allFields: string[],
  featuresString?: string
): string[] => {
  if (!featuresString) return allFields

  const processedFeatures = processGeneralSettingsFeatures(featuresString)

  // If environment doesn't match, show all fields (fallback behavior)
  if (!processedFeatures?.environmentMatch) {
    return allFields
  }

  // If module is explicitly disabled, return empty array (handled in component)
  if (processedFeatures?.classModule?.enabled === false) {
    return []
  }

  // If not in custom mode or module not properly configured, show all fields
  if (!processedFeatures?.isValid || !processedFeatures?.addClassForm?.shouldShowCustomForm) {
    return allFields
  }

  return allFields.filter((field) => processedFeatures.addClassForm.allowedColumns.includes(field))
}

/**
 * Check if a specific column should be shown in update class form
 * @param column - Column name to check
 * @param featuresString - Features string from general settings
 * @returns boolean indicating if column should be shown
 */
export const shouldShowUpdateClassColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) {
    return true
  }

  const features = parseFeatures(featuresString)
  if (!features) {
    return true
  }

  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) {
    return true
  }

  if (!features.modules || !features.modules.class_module) {
    return true
  }

  const classModule = features.modules.class_module
  if (classModule.enabled !== true) {
    return classModule.enabled === false ? false : true
  }

  if (!classModule.features || !classModule.features.update_class) {
    return true
  }

  const updateClassFeature = classModule.features.update_class

  if (updateClassFeature.mode === 'custom') {
    if (!updateClassFeature.columns || !Array.isArray(updateClassFeature.columns)) {
      return true
    }
    return updateClassFeature.columns.includes(column)
  } else if (updateClassFeature.mode === 'mini') {
    const hiddenFieldsInMini = ['label_id', 'color', 'description', 'capacity', 'room']
    return !hiddenFieldsInMini.includes(column)
  } else {
    return true
  }
}

// Subject field gating based on features
export const shouldShowAddSubjectColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.subject_module) return true
  const subjectModule = features.modules.subject_module
  if (subjectModule.enabled !== true) return subjectModule.enabled === false ? false : true
  if (!subjectModule.features || !subjectModule.features.add_subject) return true
  const addSubject = subjectModule.features.add_subject
  if (addSubject.mode === 'custom') {
    if (!addSubject.columns || !Array.isArray(addSubject.columns)) return true
    return addSubject.columns.includes(column)
  } else if (addSubject.mode === 'mini') {
    const allowedInMini = [
      'name',
      'class',
      'teacher',
      'short_name',
      'frequency',
      'duration',
      'description',
      'practical',
      'status',
    ]
    return allowedInMini.includes(column)
  }
  return true
}

export const shouldShowUpdateSubjectColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.subject_module) return true
  const subjectModule = features.modules.subject_module
  if (subjectModule.enabled !== true) return subjectModule.enabled === false ? false : true
  if (!subjectModule.features || !subjectModule.features.update_subject) return true
  const updateSubject = subjectModule.features.update_subject
  if (updateSubject.mode === 'custom') {
    if (!updateSubject.columns || !Array.isArray(updateSubject.columns)) return true
    return updateSubject.columns.includes(column)
  } else if (updateSubject.mode === 'mini') {
    const allowedInMini = [
      'name',
      'class',
      'teacher',
      'short_name',
      'frequency',
      'duration',
      'description',
      'practical',
      'status',
    ]
    return allowedInMini.includes(column)
  }
  return true
}

// Student field gating based on features
export const shouldShowAddStudentColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.student_module) return true
  const studentModule = features.modules.student_module
  if (studentModule.enabled !== true) return studentModule.enabled === false ? false : true
  if (!studentModule.features || !studentModule.features.add_student) return true
  const addStudent = studentModule.features.add_student
  if (addStudent.mode === 'custom') {
    if (!addStudent.columns || !Array.isArray(addStudent.columns)) return true
    return addStudent.columns.includes(column)
  } else if (addStudent.mode === 'mini') {
    const allowedInMini = [
      'first_name',
      'last_name',
      'student_registration_no',
      'mobile',
      'address',
      'gender',
      'registration_date',
      'class',
      'date_of_birth',
      'user_credentials',
      'username',
      'password',
      'email',
    ]
    return allowedInMini.includes(column)
  }
  return true
}

export const shouldShowUpdateStudentColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.student_module) return true
  const studentModule = features.modules.student_module
  if (studentModule.enabled !== true) return studentModule.enabled === false ? false : true
  if (!studentModule.features || !studentModule.features.update_student) return true
  const updateStudent = studentModule.features.update_student
  if (updateStudent.mode === 'custom') {
    if (!updateStudent.columns || !Array.isArray(updateStudent.columns)) return true
    return updateStudent.columns.includes(column)
  } else if (updateStudent.mode === 'mini') {
    const allowedInMini = [
      'first_name',
      'last_name',
      'student_registration_no',
      'mobile',
      'address',
      'gender',
      'registration_date',
      'class',
      'date_of_birth',
      'user_credentials',
      'username',
      'password',
      'email',
    ]
    return allowedInMini.includes(column)
  }
  return true
}

// Teacher field gating based on features
export const shouldShowAddTeacherColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.teacher_module) return true
  const teacherModule = features.modules.teacher_module
  if (teacherModule.enabled !== true) return teacherModule.enabled === false ? false : true
  if (!teacherModule.features || !teacherModule.features.add_teacher) return true
  const addTeacher = teacherModule.features.add_teacher
  if (addTeacher.mode === 'custom') {
    if (!addTeacher.columns || !Array.isArray(addTeacher.columns)) return true
    return addTeacher.columns.includes(column)
  } else if (addTeacher.mode === 'mini') {
    const allowedInMini = [
      'first_name',
      'last_name',
      'gender',
      'cnic',
      'registration_date',
      'address',
      'mobile',
      'level',
    ]
    return allowedInMini.includes(column)
  }
  return true
}

export const shouldShowUpdateTeacherColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.teacher_module) return true
  const teacherModule = features.modules.teacher_module
  if (teacherModule.enabled !== true) return teacherModule.enabled === false ? false : true
  if (!teacherModule.features || !teacherModule.features.update_teacher) return true
  const updateTeacher = teacherModule.features.update_teacher
  if (updateTeacher.mode === 'custom') {
    if (!updateTeacher.columns || !Array.isArray(updateTeacher.columns)) return true
    return updateTeacher.columns.includes(column)
  } else if (updateTeacher.mode === 'mini') {
    const allowedInMini = [
      'first_name',
      'last_name',
      'gender',
      'cnic',
      'registration_date',
      'address',
      'mobile',
      'level',
    ]
    return allowedInMini.includes(column)
  }
  return true
}

// Staff field gating based on features
export const shouldShowAddStaffColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.staff_module) return true
  const staffModule = features.modules.staff_module
  if (staffModule.enabled !== true) return staffModule.enabled === false ? false : true
  if (!staffModule.features || !staffModule.features.add_staff) return true
  const addStaff = staffModule.features.add_staff
  if (addStaff.mode === 'custom') {
    if (!addStaff.columns || !Array.isArray(addStaff.columns)) return true
    return addStaff.columns.includes(column)
  } else if (addStaff.mode === 'mini') {
    const allowedInMini = [
      'first_name',
      'last_name',
      'registration_date',
      'address',
      'mobile',
      'level',
      'role',
      'gender',
    ]
    return allowedInMini.includes(column)
  }
  return true
}

export const shouldShowUpdateStaffColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.staff_module) return true
  const staffModule = features.modules.staff_module
  if (staffModule.enabled !== true) return staffModule.enabled === false ? false : true
  if (!staffModule.features || !staffModule.features.update_staff) return true
  const updateStaff = staffModule.features.update_staff
  if (updateStaff.mode === 'custom') {
    if (!updateStaff.columns || !Array.isArray(updateStaff.columns)) return true
    return updateStaff.columns.includes(column)
  } else if (updateStaff.mode === 'mini') {
    const allowedInMini = [
      'first_name',
      'last_name',
      'registration_date',
      'address',
      'mobile',
      'level',
      'role',
      'gender',
    ]
    return allowedInMini.includes(column)
  }
  return true
}

/**
 * Check if class module features are enabled
 * @param featuresString - Features string from general settings
 * @returns boolean indicating if class module is enabled
 */
export const isClassModuleEnabled = (featuresString?: string): boolean => {
  if (!featuresString) return true // If no features configured, assume enabled

  const processedFeatures = processGeneralSettingsFeatures(featuresString)

  // If environment doesn't match, assume enabled (fallback behavior)
  if (!processedFeatures?.environmentMatch) {
    return true
  }

  // Only return false if explicitly disabled
  return processedFeatures?.classModule?.enabled !== false
}

// Grade field gating based on features
export const shouldShowAddGradeColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.grades_module) return true
  const gradesModule = features.modules.grades_module
  if (gradesModule.enabled !== true) return gradesModule.enabled === false ? false : true
  if (!gradesModule.features || !gradesModule.features.add_grade) return true
  const addGrade = gradesModule.features.add_grade
  if (addGrade.mode === 'custom') {
    if (!addGrade.columns || !Array.isArray(addGrade.columns)) return true
    return addGrade.columns.includes(column)
  } else if (addGrade.mode === 'mini') {
    const allowedInMini = ['name', 'level', 'status']
    return allowedInMini.includes(column)
  }
  return true
}

export const shouldShowUpdateGradeColumn = (column: string, featuresString?: string): boolean => {
  if (!featuresString) return true
  const features = parseFeatures(featuresString)
  if (!features) return true
  const currentEnv = getAppEnv()
  if (!features.env || features.env !== currentEnv) return true
  if (!features.modules || !features.modules.grades_module) return true
  const gradesModule = features.modules.grades_module
  if (gradesModule.enabled !== true) return gradesModule.enabled === false ? false : true
  if (!gradesModule.features || !gradesModule.features.update_grade) return true
  const updateGrade = gradesModule.features.update_grade
  if (updateGrade.mode === 'custom') {
    if (!updateGrade.columns || !Array.isArray(updateGrade.columns)) return true
    return updateGrade.columns.includes(column)
  } else if (updateGrade.mode === 'mini') {
    const allowedInMini = ['name', 'level', 'status']
    return allowedInMini.includes(column)
  }
  return true
}

export const getLevelsByBranchId = (branch_id: number): number[] => {
  const reduxStore = store.getState()
  const {levels} = reduxStore.mainReducer

  const filteredLevelIds = levels
    .filter((item: any) => item?.branch_id === branch_id)
    .map((item: any) => item?.id)

  return filteredLevelIds || []
}

// Capped at `max` letters by default -- an uncapped "Muhammad Abdul Rahman Khan" yields
// four glyphs that overflow the small avatar sizes. Pass 0 for no limit.
export const getInitials = (text: string, max = 2) => {
  const letters = text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())

  return (max > 0 ? letters.slice(0, max) : letters).join('')
}

// Redirect that preserves the current query string onto the destination path.
export const NavigateWithSearch = ({to, replace}: {to: string; replace?: boolean}) => {
  const {search} = useLocation()
  return createElement(Navigate, {to: `${to}${search}`, replace})
}

// <input type="color"> only accepts #RRGGBB, so expand a valid #RGB shorthand to its 6-digit form.
export const toColorInputHex = (hex: string): string => {
  const isValid = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)
  if (!isValid) return '#000000'
  return hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex
}

// exceljs converts Excel date cells to JS Dates anchored at UTC, so read with UTC getters.
const excelCellToString = (cell: any): string => {
  const value = cell?.value
  if (value === null || value === undefined) return ''
  if (value instanceof Date) {
    return `${value.getUTCMonth() + 1}/${value.getUTCDate()}/${value.getUTCFullYear()}`
  }
  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part: any) => part?.text ?? '').join('')
    }
    if ('result' in value) return excelCellToString({value: value.result})
    if ('text' in value) return excelCellToString({value: value.text})
    if ('error' in value) return ''
    return String(cell?.text ?? '')
  }
  return String(value)
}

// Reads the first worksheet of an .xlsx file into string rows (same shape a CSV parser returns).
/** Every row of a worksheet as trimmed-right string cells (trailing blank rows dropped). */
const excelWorksheetToRows = (worksheet: import('exceljs').Worksheet): string[][] => {
  const totalColumns = worksheet.columnCount
  const rows: string[][] = []
  worksheet.eachRow({includeEmpty: true}, (excelRow) => {
    const cells: string[] = []
    for (let col = 1; col <= totalColumns; col++) {
      cells.push(excelCellToString(excelRow.getCell(col)))
    }
    rows.push(cells)
  })
  while (rows.length && rows[rows.length - 1].every((cell) => !cell.trim())) {
    rows.pop()
  }
  return rows
}

export const excelFileToRows = async (file: File): Promise<string[][]> => {
  const {Workbook} = await import('exceljs')
  const workbook = new Workbook()
  await workbook.xlsx.load(await file.arrayBuffer())
  const worksheet = workbook.worksheets[0]
  if (!worksheet) throw new Error('No worksheet found in the Excel file')
  return excelWorksheetToRows(worksheet)
}

/**
 * Like `excelFileToRows`, plus sheets read BY NAME from the same workbook. Hidden and very
 * hidden sheets are read like any other, which is how a template's lookup sheet (the students
 * template's `Lists`) is reached. A name the workbook does not have is simply absent from
 * `sheets`.
 *
 * @returns The first sheet's rows and name, and the rows of each requested sheet that exists.
 */
export const excelFileToNamedSheets = async (
  file: File,
  sheetNames: readonly string[]
): Promise<{rows: string[][]; firstSheetName: string; sheets: Record<string, string[][]>}> => {
  const {Workbook} = await import('exceljs')
  const workbook = new Workbook()
  await workbook.xlsx.load(await file.arrayBuffer())
  const worksheet = workbook.worksheets[0]
  if (!worksheet) throw new Error('No worksheet found in the Excel file')

  const sheets: Record<string, string[][]> = {}
  sheetNames.forEach((name) => {
    const named = workbook.getWorksheet(name)
    if (named) sheets[name] = excelWorksheetToRows(named)
  })
  return {rows: excelWorksheetToRows(worksheet), firstSheetName: worksheet.name, sheets}
}

export const escapeHtmlForModal = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

type AlertModalOptions = {
  title?: string
  confirmButtonText?: string
}

// Selective AI lists surface errors/warnings in a Swal modal instead of a toast.
const fireAlertModal = (
  icon: 'error' | 'warning',
  message: unknown,
  options?: AlertModalOptions
) => {
  const text =
    typeof message === 'string'
      ? message
      : String((message as {message?: string})?.message ?? message ?? '')
  return Swal.fire({
    icon,
    title: options?.title,
    html: `<div class="fs-5 text-gray-800" style="white-space: pre-line; word-break: break-word;">${escapeHtmlForModal(
      text
    )}</div>`,
    confirmButtonText: options?.confirmButtonText ?? 'OK',
  })
}

export const showErrorModal = (message: unknown, options?: AlertModalOptions) =>
  fireAlertModal('error', message, options)

export const showWarningModal = (message: unknown, options?: AlertModalOptions) =>
  fireAlertModal('warning', message, options)

export const downloadRowsAsExcel = async (rows: string[][], fileName: string) => {
  const {Workbook} = await import('exceljs')
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet('Sheet1')
  rows.forEach((row) => worksheet.addRow(row))

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', fileName)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
